import React, { useEffect, useState } from 'react';
import { IModalWindow } from '../../modal/ModalWindow';
import {
	getFormattedInitialData, getFormattedInitialFormikData,
	getFormattedSectionFormFields, getFormikValidationSchema, getInitialFormikData, MERCHANTS_TABS,
} from '../../../utils/merchantUtils';
import { useFormik } from 'formik';
import { InitialValuesTypes } from '../../../store/merchnts/merchant-interfaces';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useParams } from 'react-router-dom';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import { isEqual } from 'lodash';
import {
	fetchMerchantData,
	setMerchantDetailsData,
	setMerchantDetailsPartnerConfigurationData,
} from '../../../store/merchnts/actions/merchant';
import { IAuthState } from '../../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../../setup';
import { checkRoles, CLAIMS } from '../../../utils/roles-helper';

export const UpdateComponent = (OriginalComponent, componentName): (props) => JSX.Element => {
	function NewComponent(props): JSX.Element {
		const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
		const { t } = useTranslation();
		const { pathname } = useLocation();
		const path = pathname.split('/')[4];
		const { id } = useParams<{ id: string }>();
		const { merchantState: { merchantData }, dispatch } = useMerchants();
		const [isEditMode, setIsEditMode] = useState<boolean>(false);
		const [isLoading, setIsLoading] = useState<boolean>(false);
		const [modalState, setModalState] = useState<IModalWindow>({
			isOpen: false
		});

		const isAdmin = checkRoles(authUser, CLAIMS.SUPER_ADMIN);

		const formik = useFormik<InitialValuesTypes | any>({
			initialValues: getInitialFormikData(componentName, isAdmin),
			validationSchema: getFormikValidationSchema(componentName, t),
			onSubmit: () => void 0
		});

		const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

		useEffect(() => {
			const initialValue = getFormattedInitialFormikData(componentName, merchantData, isAdmin);
			formik.setValues(initialValue);
		}, [merchantData]);

		const handleEditClick = async (pageName?: string): Promise<void> => {
			formik.handleSubmit();
			if (formik.isValid) {
				const data = getFormattedSectionFormFields(formik.values, componentName, merchantData, isAdmin);
				const initialData = getFormattedInitialData(merchantData, componentName, isAdmin);
				const initialValues = getFormattedInitialFormikData(componentName, merchantData, isAdmin);
				try {
					if (!isEqual(data, initialData)) {
						setIsLoading(true);
						const requestName = (typeof pageName === 'string') && pageName ? pageName : path;

						if (checkRoles(authUser, CLAIMS.PARTNER_ADMIN) && requestName === MERCHANTS_TABS.GENERAL) {
							await setMerchantDetailsPartnerConfigurationData(id, data);
						} else {
							await setMerchantDetailsData(id, data, requestName, formik);
						}
						await fetchMerchantData(dispatch, id);
					}
				} catch (e) {
					console.log('handleEditClick', e);
					formik.setValues(initialValues);
				} finally {
					setIsLoading(false);
					setIsEditMode(false);
				}
			}
		};

		const handleCancelClick = (): void => {
			const data = getFormattedSectionFormFields(formik.values, componentName, merchantData, isAdmin);
			const initialData = getFormattedInitialData(merchantData, componentName, isAdmin);
			const initialValues = getFormattedInitialFormikData(componentName, merchantData, isAdmin);
			if (!isEqual(data, initialData)) {
				setModalState({
					isOpen: true,
					modalTitle: t('MERCHANT.CANCEL_MODAL_TITLE'),
					modalText: t('MERCHANT.CANCEL_MODAL_TEXT'),
					confirmButtonText: t('MERCHANT.CANCEL_MODAL_CONFIRM_BTN_NAME'),
					onConfirm: closeModal,
					onCancel: (): void => {
						closeModal();
						setIsEditMode(false);
						formik.setValues(initialValues);
					}
				});
			} else {
				setIsEditMode(false);
			}
		};

		return (
			<OriginalComponent
				isEditMode={isEditMode}
				setIsEditMode={setIsEditMode}
				isLoading={isLoading}
				modalState={modalState}
				setModalState={setModalState}
				closeModal={closeModal}
				formik={formik}
				onEditClick={handleEditClick}
				onCancelClick={handleCancelClick}
				{...props}
			/>
		);
	}
	return NewComponent;
};

